/**
 * Phase 6.1 — Pull-forward planner (suggestion-only).
 *
 * Doctrine:
 *   - Bucket 1 §1.4    OptiFinish + bay-occupancy windows can open early
 *                      when work completes ahead of schedule.
 *   - Bucket 2 §2.7    AutoBump preserves Due Out promises while filling
 *                      capacity (the existing `present_vehicle_priority`
 *                      flow in `useAutoBump.ts:865-919` is the narrow
 *                      precedent).
 *   - Bucket 3 §3.4    Production states drive lifecycle semantics.
 *   - Bucket 4.5 §4.5.5  Due Out outranks Waiter Flex; physical
 *                        constraints outrank Due Out.
 *   - Bucket 4.5 §4.5.9  Waiter Flex must NOT extend Due Out.
 *   - Bucket 5 §5.3    Technician availability states gate pull-forward
 *                      eligibility.
 *   - Bucket 5 §5.7    Tech work capacity vs schedule/bay capacity must
 *                      remain separable (Phase 4.3 dual-capacity model).
 *
 * Why this module exists
 * ----------------------
 * The Compliance Master Fix List flagged pull-forward logic as missing.
 * Survey confirmed: the only existing code is a single narrow heuristic
 * in `src/hooks/useAutoBump.ts:865-919` ("present vehicle priority")
 * that bumps one present ticket past one non-present ticket when a
 * running timer is within N minutes of opti-finish. Same-tech /
 * same-day only. No trigger taxonomy. No candidate vocabulary. No
 * blocker codes. No future-day policy.
 *
 * Phase 6.1 supplies the canonical vocabulary + planning helpers so:
 *   - The seven pull-forward triggers (tech early-complete, early
 *     clock-in, return from absence, ticket cancel/delete, bay/lane
 *     open, hold release, parking ready) have explicit names.
 *   - The five candidate kinds (present drop-off, scheduled-but-
 *     parked, later same-day, future day, held-in-parking released)
 *     have explicit names.
 *   - Eligibility composes Phase 1.x production-state / availability,
 *     Phase 4.3 dual capacity, Phase 5.4 Due Out risk, Phase 2.4
 *     parking-to-bay confirmation, and Phase 4.1b waiter anchor doctrine
 *     without re-deriving any of them.
 *
 * Phase 6.1 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller passes
 *     `now`, the trigger context, the policy, and the candidate ticket
 *     rows.
 *   - No DB migration, no schema change, no runtime mutation wiring.
 *   - Every plan emitted has `manualOnly: true`. The narrow runtime
 *     `present_vehicle_priority` AutoBump path is UNCHANGED.
 *   - Adoption points are documented at the bottom of this file.
 *
 * Builds on:
 *   - Phase 1.1 `getProductionState`.
 *   - Phase 1.3 `isProductionRelevantState` / `isActiveScheduledProductionState`.
 *   - Phase 1.4 `canPerformActiveProductionMutation`.
 *   - Phase 4.3 `getDualCapacityVerdict` / `getCapacityBlockers`.
 *   - Phase 5.4 `getDueOutRiskExclusionReason`.
 */

import {
  getProductionState,
  isProductionRelevantState,
  type ProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';
import type { TechnicianAvailabilityState } from './technicianAvailabilityState';
import type { DualCapacityVerdict } from './dualCapacityModel';

// =============================================================================
// Trigger vocabulary
// =============================================================================

/**
 * Discrete pull-forward trigger kinds. Caller identifies which kind of
 * trigger opened the capacity window and passes it as part of
 * `PullForwardContext`. The planner does NOT subscribe to events — it
 * is a pure decision function.
 */
export type PullForwardTriggerKind =
  | 'tech_completed_early'        // running timer finished before opti-finish
  | 'tech_clocked_in_early'       // tech clocked in before scheduled shift start
  | 'tech_returned_from_absence'  // is_present flipped true after absent
  | 'ticket_deleted_or_cancelled' // upstream ticket removed from lane
  | 'bay_lane_opened_early'       // bay or lane window opened sooner than planned
  | 'hold_released'               // previously-held ticket released
  | 'parking_ready_for_pull_in';  // parked ticket signaled bay-ready

export const PULL_FORWARD_TRIGGER_KINDS = Object.freeze([
  'tech_completed_early',
  'tech_clocked_in_early',
  'tech_returned_from_absence',
  'ticket_deleted_or_cancelled',
  'bay_lane_opened_early',
  'hold_released',
  'parking_ready_for_pull_in',
] as const) satisfies ReadonlyArray<PullForwardTriggerKind>;

export interface PullForwardTrigger {
  kind: PullForwardTriggerKind;
  /** When the trigger fired / capacity opened. */
  occurredAt: Date;
  /** Tech whose lane opened (or `null` for facility-wide bay open). */
  techId?: string | null;
  /** Bay whose slot opened. */
  bayId?: string | null;
  /** Caller-computed minutes of newly available capacity in the window. */
  availableMinutes?: number;
  /** When the capacity window starts. */
  windowStart?: Date;
  /** When the capacity window ends (window end ≤ tech shift end). */
  windowEnd?: Date;
  /**
   * Core-100 R4 wave 3: the trigger's SHOP day (YYYY-MM-DD) as the server computed it (`capacity_openings.shop_day`).
   * When given it is the day compared with `scheduled_date`; the runtime's own zone is only the fallback, so a
   * browser in another zone (or near midnight) no longer calls today's work "future day".
   */
  shopDay?: string;
}

// =============================================================================
// Candidate vocabulary
// =============================================================================

/**
 * Discrete candidate kinds. Returned by `getPullForwardCandidateKind`
 * and embedded in the resulting plan. `null` means the ticket is not
 * a pull-forward candidate at all (terminal / inactive / wrong state).
 */
export type PullForwardCandidateKind =
  | 'present_dropoff'
  | 'scheduled_but_parked'
  | 'later_same_day'
  | 'future_day_allowed'
  | 'held_in_parking_released';

export const PULL_FORWARD_CANDIDATE_KINDS = Object.freeze([
  'present_dropoff',
  'scheduled_but_parked',
  'later_same_day',
  'future_day_allowed',
  'held_in_parking_released',
] as const) satisfies ReadonlyArray<PullForwardCandidateKind>;

// =============================================================================
// Blocker vocabulary
// =============================================================================

export type PullForwardBlocker =
  | 'terminal_state'
  | 'inactive_parking_lot'
  | 'estimate_not_converted'
  | 'unresolved_hold'
  | 'waiter_before_promise'
  | 'due_out_risk'
  | 'technician_unavailable'
  | 'technician_capacity_limited'
  | 'bay_capacity_blocked'
  | 'bay_type_mismatch'
  | 'parking_to_bay_confirmation_required'
  | 'running_timer_constraint'
  | 'future_day_not_allowed'
  // oct20d-sched (Founder Decision 4, rule F): a later day's work whose vehicle is not physically here.
  | 'future_day_vehicle_not_present'
  // oct20e (FD-2026-10-24-A; R4:240; CORE-BO :59): a same-day (or undated) candidate whose vehicle is not physically
  // here stays in tentative planning — it is never offered for immediate pull-forward into executable work.
  | 'vehicle_not_present'
  | 'missing_schedule_window'
  | 'no_assigned_tech'
  | 'ticket_locked'
  | 'ticket_pinned'
  | 'not_a_pull_forward_candidate';

// =============================================================================
// Plan + policy + context
// =============================================================================

export interface PullForwardPolicy {
  /**
   * Allow pull-forward of tickets whose `scheduled_date` is AFTER the
   * trigger window's date. Default `false`. Bucket 5 / shop policy.
   */
  allowFutureDay?: boolean;
  /**
   * Allow moving a waiter ticket EARLIER than its `scheduled_start_time`
   * promise. Default `false`. Bucket 2 §2.10 / Bucket 4.1 — waiter
   * promise is sacred unless customer explicitly accepts an earlier
   * appointment.
   */
  allowWaiterBeforePromise?: boolean;
  /**
   * Shop's `present_vehicle_priority` setting (minutes). Used for
   * ranking heuristics. `0` or omitted = disabled (no priority bonus
   * for present vehicles).
   */
  presentVehiclePriorityMin?: number;
}

export interface PullForwardContext {
  trigger: PullForwardTrigger;
  now: Date;
  /**
   * Tech availability state at trigger time (Phase 1.2). When provided,
   * `absent` / `off_shift` / etc. block pull-forward (Bucket 5 §5.3).
   */
  technicianAvailability?: TechnicianAvailabilityState;
  /**
   * Dual-capacity verdict (Phase 4.3). When provided, tech-blocked or
   * bay-blocked verdicts surface as blocker codes.
   */
  dualCapacity?: DualCapacityVerdict;
  /**
   * Caller-supplied: Phase 2.4 parking-to-bay confirmation is required
   * for THIS candidate. Defaults to `true` for `scheduled_but_parked`
   * candidates (the safe interpretation); set explicitly when the
   * caller has the runtime answer.
   */
  requiresBayConfirmation?: boolean;
  /**
   * For Held-in-Parking candidates: caller signals whether the hold
   * has been resolved. Default `false` — unresolved holds block.
   */
  holdResolved?: boolean;
  /**
   * Caller's projected new opti-finish if this ticket were pulled
   * forward into the trigger window. When provided AND > `ticket.due_out`,
   * the planner emits `due_out_risk`. When omitted on a ticket WITH a
   * `due_out`, the planner is conservative — it does NOT emit Due Out
   * risk for unverified moves (caller must verify before applying).
   */
  projectedNewFinish?: Date | string | null;
  /** Pull-forward policy (shop / global). */
  policy?: PullForwardPolicy;
}

export interface PullForwardCandidatePlan {
  kind: PullForwardCandidateKind | 'blocked';
  ticketId: string;
  /** Trigger kind that surfaced this plan (forwarded from context). */
  triggerKind: PullForwardTriggerKind;
  /** True iff `blockers` is empty AND the candidate kind is non-null. */
  eligible: boolean;
  blockers: PullForwardBlocker[];
  reasons: string[];
  /** Every plan in Phase 6.1 is suggestion-only. */
  manualOnly: true;
  /** Higher = preferred. Used by `rankPullForwardCandidates`. */
  score: number;
}

/**
 * Permissive ticket shape. Extends Phase 1.1's shape with the fields
 * the planner inspects for candidate-kind classification.
 */
export interface PullForwardTicketShape extends ProductionStateTicketShape {
  type?: string | null;
  due_out?: string | Date | null;
  scheduled_date?: string | null;
  scheduled_start_time?: string | Date | null;
  assigned_tech_id?: string | null;
  jobBayType?: string | null;
  lockedTechnicianId?: string | null;
  isPinned?: boolean;
  timer_status?: 'running' | 'paused' | 'stopped' | null;
}

// =============================================================================
// Internal helpers
// =============================================================================

function parseTs(v: Date | string | null | undefined): number | null {
  if (v == null) return null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

/** The trigger's shop day: the server's when supplied, else the runtime-local day of `occurredAt` (fallback). */
function triggerShopDay(trigger: PullForwardTrigger): string {
  return trigger.shopDay && /^\d{4}-\d{2}-\d{2}$/.test(trigger.shopDay) ? trigger.shopDay : toYmd(trigger.occurredAt);
}

/** Take a `Date` and return its YYYY-MM-DD shop-day string in local TZ. */
function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isVehiclePresent(ticket: PullForwardTicketShape): boolean {
  // Mirrors the AutoBump definition at useAutoBump.ts:876:
  //   present = parking_spot_id || bay_id || timer running/paused
  // For pull-forward, "present drop-off" = parked but NOT yet in bay
  // AND NOT yet started. We narrow to: parking_spot_id present, no
  // bay_id, no timer running. bay_id+running is "already in bay".
  return (
    ticket.parking_spot_id != null && !ticket.bay_id && ticket.timer_status !== 'running'
  );
}

/**
 * oct20d-sched (rule F): is a later-day ticket's vehicle physically here? The same presence the classifier reads —
 * parked (a spot or the lot) or held in parking — so a `future_day_allowed` kind (scheduled, not here) is not present.
 */
function isFutureCandidatePresent(ticket: PullForwardTicketShape): boolean {
  if (isVehiclePresent(ticket)) return true;
  const state = getProductionState(ticket);
  return state === 'scheduled_but_parked' || state === 'held_in_parking';
}

// =============================================================================
// Candidate-kind classifier
// =============================================================================

/**
 * Classify a ticket's pull-forward candidate kind. Returns `null` if
 * the ticket is NOT a pull-forward candidate at all (terminal /
 * inactive / wrong production state).
 *
 * Resolution order:
 *   1. Production state must be production-relevant (Bucket 3 §3.4) —
 *      otherwise return null.
 *   2. `held_in_parking` → 'held_in_parking_released' (eligibility
 *      depends on context.holdResolved; classifier is structural only).
 *   3. `scheduled_but_parked` → 'scheduled_but_parked'.
 *   4. `active_in_bay` / `held_in_bay` → null (already in bay; not a
 *      pull-forward candidate).
 *   5. `scheduled` with vehicle present (parking_spot_id + no bay_id +
 *      not timer-running) → 'present_dropoff'.
 *   6. `scheduled` and `scheduled_date` matches trigger date → 'later_same_day'.
 *   7. `scheduled` and `scheduled_date` later than trigger date → 'future_day_allowed'.
 *   8. anything else (estimate_prospect, parking_lot_inactive,
 *      deleted, archived, picked_up, completed_awaiting_pickup) → null.
 */
export function getPullForwardCandidateKind(
  ticket: PullForwardTicketShape,
  context: PullForwardContext,
): PullForwardCandidateKind | null {
  const state = getProductionState(ticket);
  if (!isProductionRelevantState(state)) return null;

  switch (state) {
    case 'held_in_parking':
      return 'held_in_parking_released';
    case 'scheduled_but_parked':
      // Drop-off + parked = the customer-dropped-off subset of
      // scheduled_but_parked. That is the "present drop-off" the
      // existing AutoBump `present_vehicle_priority` flow targets at
      // useAutoBump.ts:876. Non-drop-off scheduled_but_parked stays
      // as the broader confirmation-gated kind.
      if (ticket.type === 'Drop-off') return 'present_dropoff';
      return 'scheduled_but_parked';
    case 'active_in_bay':
    case 'held_in_bay':
      return null;
    case 'scheduled':
      break;
    default:
      return null;
  }

  // scheduled — drill down by physical presence + date.
  if (isVehiclePresent(ticket)) return 'present_dropoff';

  const triggerDate = triggerShopDay(context.trigger);
  if (ticket.scheduled_date && ticket.scheduled_date === triggerDate) {
    return 'later_same_day';
  }
  if (ticket.scheduled_date && ticket.scheduled_date > triggerDate) {
    return 'future_day_allowed';
  }
  // No scheduled_date — treat as same-day if the trigger doesn't say
  // otherwise. Conservative default.
  return 'later_same_day';
}

// =============================================================================
// Blocker collector
// =============================================================================

/**
 * Collect EVERY blocker the planner observes for this ticket in the
 * given context. Returns an empty array when the ticket is eligible.
 *
 * Errors are collected (not short-circuited) so UI can render all
 * problems at once.
 */
export function getPullForwardCandidateExclusionReason(
  ticket: PullForwardTicketShape,
  context: PullForwardContext,
): PullForwardBlocker[] {
  const blockers: PullForwardBlocker[] = [];

  const state = getProductionState(ticket);

  // 1. Terminal / inactive state exclusions.
  if (state === 'deleted' || state === 'archived' || state === 'picked_up') {
    blockers.push('terminal_state');
  }
  if (state === 'completed_awaiting_pickup') blockers.push('terminal_state');
  if (state === 'parking_lot_inactive') blockers.push('inactive_parking_lot');
  if (state === 'estimate_prospect') blockers.push('estimate_not_converted');

  // 2. Held-in-Parking: unresolved hold blocks.
  if (state === 'held_in_parking' && !context.holdResolved) {
    blockers.push('unresolved_hold');
  }

  // 3. Active-in-bay / held-in-bay: not a pull-forward candidate.
  if (state === 'active_in_bay' || state === 'held_in_bay') {
    blockers.push('not_a_pull_forward_candidate');
  }

  // 4. Lock / pin / running timer (per-ticket structural).
  if (ticket.lockedTechnicianId) blockers.push('ticket_locked');
  if (ticket.isPinned) blockers.push('ticket_pinned');
  if (ticket.timer_status === 'running') blockers.push('running_timer_constraint');

  // 5. Waiter-before-promise (Bucket 2 §2.10 / Bucket 4.1).
  if (ticket.type === 'Waiter' && ticket.scheduled_start_time != null) {
    const promiseMs = parseTs(ticket.scheduled_start_time);
    const windowStartMs =
      parseTs(context.trigger.windowStart) ?? context.trigger.occurredAt.getTime();
    if (
      promiseMs != null &&
      windowStartMs < promiseMs &&
      context.policy?.allowWaiterBeforePromise !== true
    ) {
      blockers.push('waiter_before_promise');
    }
  }

  // 6. Future-day policy.
  if (ticket.scheduled_date) {
    const triggerDate = triggerShopDay(context.trigger);
    if (
      ticket.scheduled_date > triggerDate &&
      context.policy?.allowFutureDay !== true
    ) {
      blockers.push('future_day_not_allowed');
    }
    // 6b. oct20d-sched — Founder Decision 4, rule F (2026-10-22): "A vehicle not physically present must not be pulled
    // forward for execution merely to fill empty time." Later-day work may be offered for today's opening only when its
    // vehicle is here (parked / in the lot — the presence the classifier reads). The shop's future-day setting does not
    // lift this: it permits later-day work, never a car that has not arrived (CORE-BO: pulling "a vehicle that is not
    // physically present" is one of the failures "worse than leaving the opening empty").
    if (ticket.scheduled_date > triggerDate && !isFutureCandidatePresent(ticket)) {
      blockers.push('future_day_vehicle_not_present');
    }
  }
  // 6c. oct20e — FD-2026-10-24-A: "A non-present vehicle may remain in tentative planning but cannot be automatically
  // dispatched into executable work"; R4:240 "A vehicle not physically present must not be pulled forward for execution
  // merely to fill empty time" (the server refuses it: reassign-ticket `vehicle_not_present`). The same rule for a
  // same-day or undated candidate — the planner used to offer a not-yet-arrived car dated today as eligible.
  if ((!ticket.scheduled_date || ticket.scheduled_date <= triggerShopDay(context.trigger))
      && !isFutureCandidatePresent(ticket)) {
    blockers.push('vehicle_not_present');
  }

  // 7. Tech availability (Phase 1.2).
  if (context.technicianAvailability) {
    switch (context.technicianAvailability) {
      case 'absent':
      case 'off_not_scheduled':
      case 'off_shift':
      case 'no_show_risk':
      case 'awaiting_clock_in':
      case 'scheduled':
        blockers.push('technician_unavailable');
        break;
      case 'on_break':
      case 'at_lunch':
        blockers.push('technician_unavailable');
        break;
      case 'capacity_limited':
        blockers.push('technician_capacity_limited');
        break;
      case 'unavailable_for_specific_job_bay':
        blockers.push('bay_type_mismatch');
        break;
      case 'present':
      case 'after_hours_active':
        break;
    }
  }

  // 8. Dual capacity (Phase 4.3).
  if (context.dualCapacity) {
    if (context.dualCapacity.technicianBlocked) {
      // The tech-availability check above usually already surfaced the
      // tech-side blocker; only push 'technician_capacity_limited' if
      // dual-capacity says limited and we haven't already pushed it.
      if (
        context.dualCapacity.technicianWorkCapacity === 'capacity_limited' &&
        !blockers.includes('technician_capacity_limited')
      ) {
        blockers.push('technician_capacity_limited');
      } else if (
        context.dualCapacity.technicianWorkCapacity === 'unavailable_for_specific_job_bay' &&
        !blockers.includes('bay_type_mismatch')
      ) {
        blockers.push('bay_type_mismatch');
      }
    }
    if (context.dualCapacity.bayBlocked) {
      if (context.dualCapacity.scheduleBayCapacity === 'bay_type_mismatch') {
        if (!blockers.includes('bay_type_mismatch')) blockers.push('bay_type_mismatch');
      } else {
        blockers.push('bay_capacity_blocked');
      }
    }
  }

  // 9. Parking-to-bay confirmation requirement (Phase 2.4).
  // `scheduled_but_parked` candidates default to requiring confirmation;
  // caller can set `requiresBayConfirmation` explicitly to override.
  const candidateKind = getPullForwardCandidateKind(ticket, context);
  if (candidateKind === 'scheduled_but_parked') {
    const required =
      context.requiresBayConfirmation === undefined
        ? true
        : context.requiresBayConfirmation === true;
    if (required) blockers.push('parking_to_bay_confirmation_required');
  } else if (
    candidateKind === 'held_in_parking_released' &&
    context.requiresBayConfirmation === true
  ) {
    blockers.push('parking_to_bay_confirmation_required');
  }

  // 10. Due Out risk (caller-supplied projection).
  if (ticket.due_out != null && context.projectedNewFinish != null) {
    const dueOutMs = parseTs(ticket.due_out);
    const projectedMs = parseTs(context.projectedNewFinish);
    if (dueOutMs != null && projectedMs != null && projectedMs > dueOutMs) {
      blockers.push('due_out_risk');
    }
  }

  // 11. Structural: no assigned tech + no trigger tech.
  if (!ticket.assigned_tech_id && !context.trigger.techId) {
    blockers.push('no_assigned_tech');
  }

  return blockers;
}

// =============================================================================
// Scoring
// =============================================================================

const BASE_SCORE_BY_KIND: Record<PullForwardCandidateKind, number> = {
  present_dropoff: 90,
  held_in_parking_released: 85,
  scheduled_but_parked: 75,
  later_same_day: 60,
  future_day_allowed: 30,
};

const REASON_BY_KIND: Record<PullForwardCandidateKind, string> = {
  present_dropoff:
    'Vehicle already on-site (drop-off) and lane capacity opened — pull forward into the bay window.',
  held_in_parking_released:
    'Held-in-Parking ticket whose hold was released — eligible to re-enter active production (Phase 2.4 confirmation may apply).',
  scheduled_but_parked:
    'Scheduled-but-Parked ticket — pull forward requires Phase 2.4 parking-to-bay confirmation before timer start.',
  later_same_day:
    'Later same-day scheduled ticket — moving earlier may fit the newly-opened window without violating Due Out / Waiter Flex.',
  future_day_allowed:
    'Future-day scheduled ticket — pull-forward only with shop policy allowFutureDay=true.',
};

// =============================================================================
// Public planning API
// =============================================================================

/**
 * Boolean: is this ticket a pull-forward candidate (eligible, no
 * blockers)? Sister to `getPullForwardPlan` for callers that only need
 * a yes/no.
 */
export function isPullForwardCandidate(
  ticket: PullForwardTicketShape,
  context: PullForwardContext,
): boolean {
  return getPullForwardPlan(ticket, context).eligible;
}

/** True iff Due Out risk is NOT among the blockers for this candidate. */
export function canPullForwardWithoutDueOutRisk(
  ticket: PullForwardTicketShape,
  context: PullForwardContext,
): boolean {
  const blockers = getPullForwardCandidateExclusionReason(ticket, context);
  return !blockers.includes('due_out_risk');
}

/** True iff Waiter-before-promise is NOT among the blockers. */
export function canPullForwardWithoutWaiterRisk(
  ticket: PullForwardTicketShape,
  context: PullForwardContext,
): boolean {
  const blockers = getPullForwardCandidateExclusionReason(ticket, context);
  return !blockers.includes('waiter_before_promise');
}

/** True iff no tech/bay capacity blockers. */
export function canPullForwardWithoutCapacityViolation(
  ticket: PullForwardTicketShape,
  context: PullForwardContext,
): boolean {
  const blockers = getPullForwardCandidateExclusionReason(ticket, context);
  return (
    !blockers.includes('technician_unavailable') &&
    !blockers.includes('technician_capacity_limited') &&
    !blockers.includes('bay_capacity_blocked') &&
    !blockers.includes('bay_type_mismatch')
  );
}

/**
 * Build the full pull-forward plan for one ticket. Returns a single
 * plan object: either an eligible plan (`kind = <candidateKind>`,
 * `eligible = true`, `blockers = []`) or a blocked plan
 * (`kind = 'blocked'`, `eligible = false`, `blockers = [...]`).
 */
export function getPullForwardPlan(
  ticket: PullForwardTicketShape,
  context: PullForwardContext,
): PullForwardCandidatePlan {
  const candidateKind = getPullForwardCandidateKind(ticket, context);
  const blockers = getPullForwardCandidateExclusionReason(ticket, context);

  // A null candidateKind means the ticket isn't a pull-forward candidate
  // at all (e.g., terminal state). Force a blocker code in that case.
  if (candidateKind == null && !blockers.includes('not_a_pull_forward_candidate')) {
    blockers.push('not_a_pull_forward_candidate');
  }

  const eligible = candidateKind != null && blockers.length === 0;

  if (!eligible) {
    return {
      kind: 'blocked',
      ticketId:
        typeof (ticket as any).id === 'string' ? (ticket as any).id : '',
      triggerKind: context.trigger.kind,
      eligible: false,
      blockers,
      reasons: blockers.map(b => `Blocked: ${b}`),
      manualOnly: true,
      score: 0,
    };
  }

  // Eligible plan.
  const kind = candidateKind as PullForwardCandidateKind;
  let score = BASE_SCORE_BY_KIND[kind];

  // Heuristic bonus: when the policy has presentVehiclePriorityMin > 0,
  // give present drop-offs a bonus that scales with the policy value.
  if (
    kind === 'present_dropoff' &&
    typeof context.policy?.presentVehiclePriorityMin === 'number' &&
    (context.policy.presentVehiclePriorityMin as number) > 0
  ) {
    score += 5;
  }

  return {
    kind,
    ticketId:
      typeof (ticket as any).id === 'string' ? (ticket as any).id : '',
    triggerKind: context.trigger.kind,
    eligible: true,
    blockers: [],
    reasons: [REASON_BY_KIND[kind]],
    manualOnly: true,
    score,
  };
}

/**
 * Rank a list of candidate tickets for pull-forward. Returns one plan
 * per ticket; eligible plans first (highest score first), then blocked
 * plans (highest base-kind score first).
 */
export function rankPullForwardCandidates(
  tickets: ReadonlyArray<PullForwardTicketShape>,
  context: PullForwardContext,
): PullForwardCandidatePlan[] {
  const plans = tickets.map(t => getPullForwardPlan(t, context));
  // oct20e — CORE-BO Operational Flow :130 "physically present work preferred; future-day work ranked last": every
  // offered candidate is now present (vehicle_not_present / future_day_vehicle_not_present block the rest), so a
  // later-day present car and a same-day present car share a kind; on an equal score the earlier day ranks first.
  const dayOf = new Map(plans.map((p, i) => [p, tickets[i]?.scheduled_date ?? '']));
  return plans.sort((a, b) => {
    if (a.eligible !== b.eligible) return a.eligible ? -1 : 1;
    if (b.score !== a.score) return b.score - a.score;
    const da = dayOf.get(a) ?? '';
    const db = dayOf.get(b) ?? '';
    return da < db ? -1 : da > db ? 1 : 0;
  });
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. UI (smallest next step):
//    Render `rankPullForwardCandidates(tickets, context)` in a board-
//    side "Open capacity" panel triggered by any of the seven kinds.
//    Show eligible plans (kind + reasons + score) and a collapsed
//    "Why not?" list of blocked plans. Every plan has `manualOnly:
//    true` — do NOT render auto-apply.
//
// 2. Narrowing `useAutoBump.ts:865-919`:
//    Replace the present-vehicle-priority heuristic's inline checks
//    with a `getPullForwardPlan(...)` call. The existing reassign-
//    ticket invocation stays untouched; the planner just NARROWS the
//    set of candidates it considers by routing through the full
//    blocker matrix (Due Out / Waiter / dual capacity / parking
//    confirmation). Behavior change: stricter pre-flight; never
//    relaxes the existing gate.
//
// 3. Trigger event listeners:
//    Tech-completes-early already fires through AutoBump. The other
//    six trigger kinds need event sources:
//      - tech_clocked_in_early       → time_clock_entries INSERT trigger
//      - tech_returned_from_absence  → technicians is_present UPDATE trigger
//      - ticket_deleted_or_cancelled → tickets soft-delete + status='Cancelled'
//      - bay_lane_opened_early       → bay assignment release events
//      - hold_released               → tickets pause_reason / paused_at clear
//      - parking_ready_for_pull_in   → parking-confirmation UI signal
//    Each can call `rankPullForwardCandidates` and surface the panel.
//
// 4. Phase 5.3 corrective-action planner integration:
//    `dueOutCorrectiveActionPlanner.parking_to_bay_acceleration` and
//    Phase 6.1 `parking_ready_for_pull_in` overlap. A future round
//    can route one to the other so a Due Out miss caused by a parked
//    vehicle triggers both planners cooperatively.
//
// 5. Future-day policy surface:
//    `policy.allowFutureDay` is shop-level. Surface a Shop Culture
//    setting `pull_forward_allow_future_day` (boolean) and route it
//    through `shopCulture` → `PullForwardPolicy`.
//
// 6. Telemetry:
//    Log `plan.kind` + `plan.blockers` distribution per trigger so
//    production data drives Phase 6.2+ prioritization (which triggers
//    actually fire, which blockers dominate).
